//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { ERC20Wei } from './conversions'
import { yupErc20WeiRequired } from './Enums'
import {
  PoolBaseAccountCTO,
  PoolBaseAccountCTOSchemaValues,
  PoolBaseAccountSummaryCTOSchemaValues,
} from './PoolBaseAccountCTO'
import { ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData } from './SchemaTypes'

export const PoolFlexAccountOnlyCTOSchemaValues = {
  dailyInterestRate: yupErc20WeiRequired
    .label('Single Day Interest Rate')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'dailyInterestRate',
        description: 'Interest rate earned in the day',
        stringType: StringTypeEnum.SINGLE_DAY_APR,
      })
    ),
  maxWithdrawRequest: yupErc20WeiRequired
    .label('Max Withdrawal Request')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'maxWithdrawRequest',
        description: 'The maximum amount of assets you can request to be withdrawn.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  maxRedeemRequest: yupErc20WeiRequired
    .label('Max Tokens Redeemable')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'maxRedeemRequest',
        description:
          'The maximum amount that can be requested for withdrawal by the account, denominated in Vault Tokens. ',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
  requestedSharesOf: yupErc20WeiRequired
    .label('Outstanding Withdrawal Requests (Tokens)')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'requestedSharesOf',
        description: 'The amount of outstanding withdrawal requests for the account, denominated in Vault Tokens. ',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
  requestedAssetsOf: yupErc20WeiRequired
    .label('Outstanding Withdrawal Requests (Assets)')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'requestedAssetsOf',
        description: 'The amount of withdrawal request currently pending repayment.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  principalEarningInterest: yupErc20WeiRequired
    .label('Principal Earning Interest')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'principalEarningInterest',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 10,
        description: 'Your current amount of loan principal earning the Loan Fee Rate.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  interestAccrued: yupErc20WeiRequired
    .label('Interest Accrued')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'interestAccrued',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 11,
        description: 'The amount of interest you have accrued to date.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  assetsDeposited: yupErc20WeiRequired
    .label('Assets Loaned')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'assetsDeposited',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 13,
        description: 'The amount of assets you have loaned to date.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  assetsWithdrawn: yupErc20WeiRequired
    .label('Assets Withdrawn')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'assetsWithdrawn',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 14,
        description: 'The amount of your loans that have been repaid to date.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  sharesTransitioningIn: yupErc20WeiRequired
    .label('Loans Pending (Tokens)')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'sharesTransitioningIn',
        description:
          'The amount of loans which are currently pending for the account, denominated in vault tokens. The tokens will begin accruing interest after the transferInDays lapse. ',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
  assetsTransitioningIn: yupErc20WeiRequired
    .label('Loans Pending (Assets)')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'assetsTransitioningIn',
        description:
          'The amount of the loans which are currently pending. The assets will begin accruing interest after the Advance Processing Time lapses.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  assetsDueForWithdraws: yupErc20WeiRequired
    .label('Withdrawal Requests Due')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'assetsDueForWithdraws',
        description: 'The amount of assets currently due to be repaid.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  accountOutstandingLoanPrincipals: yupErc20WeiRequired
    .label('Outstanding Loan Principals')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'accountOutstandingLoanPrincipals',
        description: 'PrincipalEarningInterest +AssetsTransitioningIn + AssetsDueForWithdraws',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  interestInDay: yupErc20WeiRequired
    .label('Interest on Day')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'interestInDay',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 12,
        description: 'Interest earned in the day',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
}

export const PoolFlexAccountSummaryCTOSchema = yup
  .object({
    ...PoolBaseAccountSummaryCTOSchemaValues,
  })
  .label('PoolFlexAccountSummaryCTO')
  .meta(
    toFieldMetaData({
      propName: 'PoolFlexAccountSummaryCTO',
      map: 'PoolFlexAccountCTO',
      description: 'Pool Flex Account CTO',
    })
  )
export const PoolFlexAccountCTOSchema = yup
  .object({
    ...PoolBaseAccountCTOSchemaValues,
    ...PoolFlexAccountOnlyCTOSchemaValues,
  })
  .label('PoolFlexAccountCTO')
  .meta(toFieldMetaData({ propName: 'PoolFlexAccountCTO', description: 'Pool Account CTO' }))

export const PoolFlexAccountOnlyCTOSchema = yup.object({
  ...PoolFlexAccountOnlyCTOSchemaValues,
})

export interface PoolFlexAccountSummaryCTO extends InferType<typeof PoolFlexAccountSummaryCTOSchema> {}
export interface PoolFlexAccountCTO extends PoolBaseAccountCTO {
  dailyInterestRate: ERC20Wei
  maxWithdrawRequest: ERC20Wei
  maxRedeemRequest: ERC20Wei
  requestedAssetsOf: ERC20Wei
  requestedSharesOf: ERC20Wei
  principalEarningInterest: ERC20Wei
  interestAccrued: ERC20Wei
  interestInDay: ERC20Wei
  assetsDeposited: ERC20Wei
  assetsWithdrawn: ERC20Wei
  sharesTransitioningIn: ERC20Wei
  assetsTransitioningIn: ERC20Wei
  assetsDueForWithdraws: ERC20Wei
  accountOutstandingLoanPrincipals: ERC20Wei
}

type PoolFlexAccountCTOInfer = InferType<typeof PoolFlexAccountCTOSchema>
export const _testAPoolFLexAccount: PoolFlexAccountCTO = {} as unknown as PoolFlexAccountCTOInfer
export const _testBPoolFlexAccount: PoolFlexAccountCTOInfer = {} as unknown as PoolFlexAccountCTO
